/**
 * Client for POST /ai/extract (services/ai): upload a receipt, get the fields back.
 *
 * The service holds the OCR key and verifies the PocketBase token, so the browser never talks to
 * the model provider. What comes back is a suggestion: it is written to the document as `review`,
 * never as `booked`.
 */
import type { VatLine } from "@/lib/at/vat";
import { parseVatLines, round2, totalsFromLines } from "@/lib/at/vat";
import { env } from "@/lib/platform/env";
import { pb } from "@/lib/platform/pocketbase";
import {
  fetchOriginals,
  getFileUrl,
  type DocumentInput,
  type DocumentKind,
  type DocumentRecord,
  type ExtractionPayload,
  type ScanCorner,
  type ScanPage,
} from "./documents";

export type ExtractedFields = {
  invoiceNumber?: string;
  partnerName?: string;
  partnerVatId?: string;
  partnerAddress?: string;
  partnerIban?: string;
  issueDate?: string;
  serviceDate?: string;
  dueDate?: string;
  currency?: string;
  netTotal?: number;
  vatTotal?: number;
  grossTotal?: number;
  vatLines?: VatLine[];
  taxTreatment?: DocumentInput["taxTreatment"];
  paymentMethod?: DocumentInput["paymentMethod"];
  reference?: string;
  tip?: number;
};

export type ExtractionResult = {
  fields: ExtractedFields;
  sources: Record<string, string>;
  confidence: number;
  text: string;
  code: { type: "rksv" | "epc" | "unknown"; payload: string } | null;
  warnings: string[];
  /** For photos: the straightened scan (PDF, base64) and how each photo became a page. */
  scan?: { pdf: string; pages: ScanPage[] };
};

export type ExtractHints = {
  kind: DocumentKind;
  ownVatId?: string;
  ownIban?: string;
  /** The business's own names, one per line: never read as the supplier. */
  ownNames?: string;
};

/** The business's trading and legal name, as `ownNames` expects them. */
export function ownNames(workspace?: { name?: string; legalName?: string } | null): string {
  return [...new Set([workspace?.name, workspace?.legalName].map((name) => name?.trim()))]
    .filter(Boolean)
    .join("\n");
}

export type ExtractOptions = {
  /** Further photos of the same receipt. */
  pages?: File[];
  /** Per photo: corners a person set, or null for the whole photo. Absent: detect them. */
  corners?: Array<ScanCorner[] | null>;
  /** The phone made the scan already: read the photos, but send no scan PDF back. */
  scannedOnPhone?: boolean;
};

export async function extractDocument(
  file: File,
  hints: ExtractHints,
  options: ExtractOptions = {},
): Promise<ExtractionResult> {
  const form = new FormData();
  form.set("file", file);
  for (const page of options.pages ?? []) form.append("pages", page);
  if (options.corners) form.set("corners", JSON.stringify(options.corners));
  if (options.scannedOnPhone) form.set("scan", "client");
  form.set("kind", hints.kind);
  if (hints.ownVatId) form.set("ownVatId", hints.ownVatId);
  if (hints.ownIban) form.set("ownIban", hints.ownIban);
  if (hints.ownNames) form.set("ownNames", hints.ownNames);

  const res = await fetch(`${env.aiUrl}/extract`, {
    method: "POST",
    headers: { Authorization: `Bearer ${pb.authStore.token}` },
    body: form,
  });
  if (!res.ok) {
    let message = `Extraction failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // keep the status message
    }
    throw new Error(message);
  }
  return (await res.json()) as ExtractionResult;
}

/** The subset of a document the extraction may fill in, plus the extraction record itself. */
export type ExtractionPatch = Partial<DocumentInput> & {
  ocrStatus: "done" | "failed";
  ocrText: string;
  qrPayload: string;
  extractionConfidence: number;
  extracted: ExtractionPayload;
};

/**
 * Turns an extraction into a document patch.
 *
 * Only empty fields are filled: once someone has typed a supplier name, a re-run must not
 * overwrite it. The status moves to `review` because every value still needs confirming.
 */
export function extractionToPatch(
  result: ExtractionResult,
  current: Partial<DocumentInput> = {},
): ExtractionPatch {
  const fields = result.fields ?? {};
  const patch: Partial<DocumentInput> = {};

  const text = <K extends keyof DocumentInput>(key: K, value: string | undefined) => {
    const existing = current[key];
    if (value && !(typeof existing === "string" && existing.trim().length > 0)) {
      Object.assign(patch, { [key]: value });
    }
  };
  const amount = <K extends keyof DocumentInput>(key: K, value: number | undefined) => {
    const existing = current[key];
    if (value !== undefined && !(typeof existing === "number" && existing !== 0)) {
      Object.assign(patch, { [key]: round2(value) });
    }
  };

  text("invoiceNumber", fields.invoiceNumber);
  text("partnerName", fields.partnerName);
  text("partnerVatId", fields.partnerVatId);
  text("partnerAddress", fields.partnerAddress);
  text("partnerIban", fields.partnerIban);
  text("issueDate", fields.issueDate);
  text("serviceDate", fields.serviceDate);
  text("dueDate", fields.dueDate);
  text("currency", fields.currency);
  if (fields.taxTreatment) text("taxTreatment", fields.taxTreatment);
  if (fields.paymentMethod) text("paymentMethod", fields.paymentMethod);

  const lines = parseVatLines(fields.vatLines);
  if (lines.length > 0 && (current.vatLines ?? []).length === 0) {
    patch.vatLines = lines;
    const totals = totalsFromLines(lines);
    amount("netTotal", fields.netTotal ?? totals.netTotal);
    amount("vatTotal", fields.vatTotal ?? totals.vatTotal);
    amount("grossTotal", fields.grossTotal ?? totals.grossTotal);
  } else {
    amount("netTotal", fields.netTotal);
    amount("vatTotal", fields.vatTotal);
    amount("grossTotal", fields.grossTotal);
  }
  amount("tip", fields.tip);

  return {
    ...patch,
    status: "review",
    ocrStatus: "done",
    ocrText: (result.text ?? "").slice(0, 50_000),
    qrPayload: result.code?.payload?.slice(0, 2000) ?? "",
    extractionConfidence: result.confidence ?? 0,
    extracted: {
      fields: fields as Record<string, unknown>,
      sources: result.sources ?? {},
      confidence: result.confidence ?? 0,
      warnings: result.warnings ?? [],
      code: result.code ?? null,
    },
  };
}

/**
 * The scan as the document's main file. Named after what was uploaded ("IMG_0042.pdf"), so the
 * list and the accountant's export still show where it came from.
 */
export function scanToPatch(
  result: ExtractionResult,
  originalName: string,
): { file: File; scan: ScanPage[] } | Record<string, never> {
  if (!result.scan) return {};
  const binary = atob(result.scan.pdf);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const base = originalName.replace(/\.[^.]+$/, "") || "scan";
  return {
    file: new File([bytes], `${base}.pdf`, { type: "application/pdf" }),
    scan: result.scan.pages,
  };
}

/**
 * Reads a stored document again. A photographed receipt is redone from its original photos -
 * with `corners` when a person corrected the crop, otherwise with the corners set before by a
 * person, otherwise detected afresh - and its scan replaced. A re-run is explicit, so it may
 * overwrite what a previous run wrote.
 */
export async function rereadDocument(
  document: DocumentRecord,
  hints: ExtractHints,
  corners?: Array<ScanCorner[] | null>,
) {
  const originals = await fetchOriginals(document);
  if (originals.length > 0) {
    const [first, ...pages] = originals as [File, ...File[]];
    const kept = document.scan?.some((page) => !page.detected)
      ? document.scan.map((page) => page.corners)
      : undefined;
    const result = await extractDocument(first, hints, { pages, corners: corners ?? kept });
    // A tip typed at the table is a person's entry, not an earlier reading: it stays.
    return {
      result,
      patch: {
        ...extractionToPatch(result, { tip: document.tip ?? 0 }),
        ...scanToPatch(result, document.originalName),
      },
    };
  }
  const blob = await (await fetch(await getFileUrl(document))).blob();
  const file = new File([blob], document.originalName || document.file, { type: blob.type });
  const result = await extractDocument(file, hints);
  return { result, patch: extractionToPatch(result) };
}
